//
import createCachedSelector from "re-reselect"
import { List, Map } from "immutable"

// selectors
import { selectCurrentCount, selectIsCountLoading } from "shared/search/selectors"
import { selectCustomFieldNameDict } from "shared/customFields/selectors"

// helpers
import isFeatureEnabled from "shared/featureflags/helperFunctions"

export const selectSearchState = (state) => state.search

export const selectBulkActionsState = (state, props) =>
    selectSearchState(state).getIn(["filterableListViews", props.uniqueFlvId, "bulkActions"], Map())

export const selectBulkActionsSlice = (sliceName, defaultGet = null) =>
    createCachedSelector(selectBulkActionsState, (state) => state.get(sliceName, defaultGet))(
        (state, props) => props.uniqueFlvId,
    )

// select items from bulkActionsDefault
export const selectIsBulkActionsSubmittable = selectBulkActionsSlice("isBulkActionsSubmittable", false)

export const selectBulkActionsActiveSidebarButton = selectBulkActionsSlice("activeSidebarButton", 0)

export const selectBulkActionsBulkActionType = createCachedSelector(
    selectBulkActionsActiveSidebarButton,
    (activeSidebarButton) =>
        activeSidebarButton ? DjangIO.app.search.bulkactions.types.BulkActionType.by_value(activeSidebarButton) : false,
)((state, props) => props.uniqueFlvId)

// select items from bulkActionsDefault/bulkActionsFilters
export const selectBulkActionsAddedIds = createCachedSelector(selectBulkActionsState, (state) =>
    state.getIn(["bulkActionsFilters", "addedIds"], List()).toJS(),
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsDeletedIds = createCachedSelector(selectBulkActionsState, (state) =>
    state.getIn(["bulkActionsFilters", "deletedIds"], List()).toJS(),
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsCount = createCachedSelector(selectBulkActionsState, (state) =>
    state.getIn(["bulkActionsFilters", "bulkActionsCount"], 0),
)((state, props) => props.uniqueFlvId)

export const selectIsInlineBulkActionsSelected = createCachedSelector(
    [selectBulkActionsState, (state, props) => props.datum],
    (bulkActionsSlice, datum) => {
        const id = datum.get("id")
        const inAddedIds = bulkActionsSlice.getIn(["bulkActionsFilters", "addedIds"], List()).includes(id)
        const inDeletedIds = bulkActionsSlice.getIn(["bulkActionsFilters", "deletedIds"], List()).includes(id)
        const isSelectAll = bulkActionsSlice.getIn(["isBulkActionsSelectAll"], false)

        return (!isSelectAll && inAddedIds) || (isSelectAll && !inDeletedIds)
    },
)((state, props) => `${props.uniqueFlvId},${props.datum.get("id")}`)

export const selectBulkActionsSelectButtonLabel = createCachedSelector(
    [selectBulkActionsCount, selectCurrentCount, selectIsCountLoading],
    (bulkActionsCount, currentCount, isCountLoading) => {
        if (isCountLoading) {
            return "Loading..."
        } else if (bulkActionsCount === currentCount) {
            return "Clear Selections"
        } else if (bulkActionsCount) {
            return "Clear Selections"
        } else if (currentCount) {
            return "Select All"
        }

        return "Nothing to Select!"
    },
)((state, props) => props.uniqueFlvId)

// the form for BulkActionsApprove and BulkActionsArchive will always be pristine
// since there is no user input involved
export const selectBulkActionsIgnorePristineCases = createCachedSelector(
    selectBulkActionsBulkActionType,
    (bulkActionType) => {
        switch (bulkActionType) {
            case DjangIO.app.search.bulkactions.types.BulkActionType.approve:
            case DjangIO.app.search.bulkactions.types.BulkActionType.archive:
            case DjangIO.app.search.bulkactions.types.BulkActionType.external_contact:
            case DjangIO.app.search.bulkactions.types.BulkActionType.unsuppress:
                return true
            default:
                return false
        }
    },
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsCombinedCount = createCachedSelector(
    [selectBulkActionsCount, selectCurrentCount],
    (bulkActionsCount, currentCount) => bulkActionsCount || currentCount,
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsQdtLabel = createCachedSelector(
    [
        selectBulkActionsCombinedCount,
        (state, props) => DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType),
    ],
    (count, qdt) => (count > 1 ? qdt.plural : qdt.singular),
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsSubmitButtonLabel = createCachedSelector(
    [
        selectIsCountLoading,
        selectBulkActionsCount,
        (state, props) => DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType),
        (state, props) => props.pristine,
        selectBulkActionsIgnorePristineCases,
        (state, props) => props.invalid,
        selectCurrentCount,
        selectBulkActionsBulkActionType,
        selectBulkActionsCombinedCount,
        selectBulkActionsQdtLabel,
    ],
    (
        isCountLoading,
        bulkActionsCount,
        qdt,
        pristine,
        ignorePristineCases,
        invalid,
        currentCount,
        bulkActionType,
        count,
        qdtLabel,
    ) => {
        if (isCountLoading) {
            return "Loading..."
        } else if (!bulkActionsCount) {
            return `Please select at least one ${qdt.singular}!`
        } else if ((pristine && !ignorePristineCases) || invalid) {
            return "Please select an option above!"
        } else if (!bulkActionsCount) {
            return `Please select at least one ${qdt.singular}!`
        } else if (count) {
            const label = [bulkActionType.submit_label]

            if (((currentCount && !bulkActionsCount) || currentCount === bulkActionsCount) && bulkActionsCount > 1) {
                label.push("All")
            }
            label.push(count.toLocaleString(), qdtLabel)

            return label.join(" ")
        }

        return "Nothing to Submit!"
    },
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsSubmitButtonSwalText = createCachedSelector(
    [
        selectBulkActionsCount,
        (state, props) => DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType),
        selectBulkActionsBulkActionType,
        selectBulkActionsCombinedCount,
        selectBulkActionsQdtLabel,
    ],
    (bulkActionsCount, qdt, bulkActionType, count, qdtLabel) => {
        const softLimit = bulkActionType.limit
        const hardLimit = bulkActionType.hard_limit
        const userLimit = Userdata.Permissions.bulk_action_max_size

        if (bulkActionsCount > hardLimit) {
            return (
                `Warning! This Bulk Action for your selection of ${qdtLabel} ` +
                `is too large to process. \n\n` +
                `Please update your search to include fewer than ${hardLimit.toLocaleString()} ${qdtLabel} and please try again.`
            )
        } else if (bulkActionsCount >= userLimit) {
            return (
                `This Bulk Action for your selection of ${qdtLabel} ` +
                `is larger than the maximum size that your account is allowed to perform. \n\n` +
                `Please either contact your administrator to increase this limit ` +
                `or update your search to include ${userLimit.toLocaleString()} or fewer ${qdtLabel} and please try again.`
            )
        } else if (bulkActionsCount >= softLimit) {
            return (
                `Your selection of ${qdtLabel} ` +
                `is too large to be processed immediately. \n\n` +
                `We will queue this up for you once you hit submit, ` +
                `but due to its complexity, your action may take a couple of hours to fully process.\n\n` +
                `This will all happen automatically, so please continue submitting other Bulk Actions in the meantime!`
            )
        }

        const swalText = [
            `You are about to ${bulkActionType.submit_label} ` +
                `${count.toLocaleString()} ${qdtLabel}.\n\n` +
                `This cannot be undone.`,
        ]

        return swalText.join("")
    },
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsSubmitButtonSwalTitle = createCachedSelector(
    [selectBulkActionsCount, selectBulkActionsBulkActionType],
    (bulkActionsCount, bulkActionType) =>
        bulkActionsCount <= bulkActionType.hard_limit && bulkActionsCount <= Userdata.Permissions.bulk_action_max_size
            ? "Are you sure that you want to submit this action?"
            : "This action is too large to submit",
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsSubmitButtonSwalButtons = createCachedSelector(
    [selectBulkActionsCount, selectBulkActionsBulkActionType],
    (bulkActionsCount, bulkActionType) => {
        const buttons = {}

        if (
            bulkActionsCount > bulkActionType.hard_limit ||
            bulkActionsCount > Userdata.Permissions.bulk_action_max_size
        ) {
            buttons.cancel = "Ok"
        } else {
            buttons.cancel = "Cancel"
            buttons.confirm = {
                text: "Submit",
                closeModal: false,
                value: true,
            }
        }

        return buttons
    },
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsSubmitButtonIcon = createCachedSelector(
    [selectBulkActionsCount, selectBulkActionsBulkActionType],
    (bulkActionsCount, bulkActionType) => {
        if (
            bulkActionsCount > bulkActionType.hard_limit ||
            bulkActionsCount > Userdata.Permissions.bulk_action_max_size
        ) {
            return "error"
        } else if (bulkActionsCount > bulkActionType.limit) {
            return "warning"
        }

        return "info"
    },
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsSubmitButtonIsDisabled = createCachedSelector(
    [
        selectCurrentCount,
        selectBulkActionsCount,
        selectIsCountLoading,
        selectBulkActionsIgnorePristineCases,
        (state, props) => props.pristine,
        (state, props) => props.invalid,
    ],
    (currentCount, bulkActionsCount, isCountLoading, ignorePristineCases, pristine, invalid) =>
        !currentCount ||
        !bulkActionsCount ||
        // if we have not made a selection
        (pristine && !ignorePristineCases) ||
        // if a selection we made is invalid (see validate() in shared/forms/BulkActions/action-creators)
        invalid ||
        // if we already have a bulkActionsCount,
        // but we then apply a filter and need to wait for a new estimated count
        isCountLoading,
)((state, props) => props.uniqueFlvId)

export const selectBulkActionsValidOptions = createCachedSelector(
    [
        (state, props) => props.quorumDataType,
        (state, props) => selectCustomFieldNameDict(props.quorumDataType)(state, props),
    ],
    (qdt, customFieldNameDict) =>
        DjangIO.app.search.bulkactions.types.BulkActionType.items()
            .filter((item) => {
                if (
                    // if there are no custom fields defined for the dataset,
                    // do not show the custom fields Bulk Action button in the sidebar
                    (item.value === DjangIO.app.search.bulkactions.types.BulkActionType.custom_field.value &&
                        customFieldNameDict &&
                        !Object.keys(customFieldNameDict).length) ||
                    // if the user does not have access to team permissioning in forms,
                    // do not show the team permissioning Bulk Action button in the sidebar
                    (item.value === DjangIO.app.search.bulkactions.types.BulkActionType.permission.value &&
                        Userdata.should_hide_team_permissionining_in_forms) ||
                    // if the user is not a superuser, do not show admin-specific Bulk Actions
                    (item.admin && !Userdata.is_quorum_employee && !Userdata.is_staff && !Userdata.logged_in_as_user)
                ) {
                    return false
                    // if the dataset is supported by the action and it is not feature flagged,
                    // show the button in the sidebar
                } else if (
                    item.supported_data_types &&
                    item.supported_data_types.includes(qdt) &&
                    (item.featureFlag ? isFeatureEnabled(item.featureFlag) : true)
                ) {
                    return true
                }

                return false
            })
            // sort Bulk Action types alphabetically
            .sort(DjangIO.stringValueSort),
)((state, props) => props.uniqueFlvId)
